'use client';

import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { normalizeMcpPromptTemplateBodyForDisplay } from '@terra/shared-contracts-mcp-servers';
import {
  DrawerCollapsible,
  MarkdownPreview,
  Text,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  getAiComponentMetadataString,
  getMcpJsonMetadataBlock,
  parseAiComponentMetadata,
} from '../utils/aiComponentMetadata';
import { McpIdentityAccessDetails } from './McpIdentityAccessDetails';

type AiApplicationMcpComponentDetailsProps = {
  component: AiComponentDto;
};

function formatJsonBlock(value: unknown): string {
  return `\`\`\`json\n${JSON.stringify(value, null, 2)}\n\`\`\``;
}

export function AiApplicationMcpComponentDetails({
  component,
}: AiApplicationMcpComponentDetailsProps) {
  const t = useTranslations('aiApplications.componentDrawer.mcp');

  if (component.type === 'MCP_TOOL') {
    const declared = getMcpJsonMetadataBlock({
      metadata: component.metadata,
      field: 'declared',
    });
    const observed = getMcpJsonMetadataBlock({
      metadata: component.metadata,
      field: 'observed',
    });

    return (
      <VStack gap={4} alignItems="stretch">
        <McpIdentityAccessDetails component={component} />
        {declared ? (
          <DrawerCollapsible title={t('declaredTool')} defaultOpen>
            <MarkdownPreview source={formatJsonBlock(declared)} compact />
          </DrawerCollapsible>
        ) : null}
        {observed ? (
          <DrawerCollapsible title={t('observedTool')} defaultOpen>
            <MarkdownPreview source={formatJsonBlock(observed)} compact />
          </DrawerCollapsible>
        ) : null}
      </VStack>
    );
  }

  if (component.type === 'MCP_PROTOCOL') {
    const metadata = parseAiComponentMetadata(component.metadata);
    const protocolDetails = {
      jsonRpcMethod: metadata.jsonRpcMethod ?? component.name,
      probeStatus: metadata.probeStatus ?? null,
      declaredInCapabilities: metadata.declaredInCapabilities ?? null,
      capabilityFamily: metadata.capabilityFamily ?? null,
    };

    return (
      <VStack gap={4} alignItems="stretch">
        <McpIdentityAccessDetails component={component} />
        <DrawerCollapsible title={t('protocolDetails')} defaultOpen>
          <MarkdownPreview source={formatJsonBlock(protocolDetails)} compact />
        </DrawerCollapsible>
      </VStack>
    );
  }

  if (component.type === 'MCP_PROMPT') {
    const metadata = parseAiComponentMetadata(component.metadata);
    const promptArguments = metadata.arguments ?? [];
    const templateBody =
      typeof metadata.templateBody === 'string'
        ? normalizeMcpPromptTemplateBodyForDisplay(metadata.templateBody)
        : null;

    return (
      <VStack gap={4} alignItems="stretch">
        <McpIdentityAccessDetails component={component} />
        <DrawerCollapsible title={t('promptDetails')} defaultOpen>
          <MarkdownPreview source={formatJsonBlock(promptArguments)} compact />
        </DrawerCollapsible>
        {templateBody ? (
          <DrawerCollapsible title={t('templateBody')} defaultOpen>
            <MarkdownPreview
              source={`\`\`\`\n${templateBody}\n\`\`\``}
              compact
            />
          </DrawerCollapsible>
        ) : null}
      </VStack>
    );
  }

  if (component.type === 'MCP_RESOURCE') {
    const metadata = parseAiComponentMetadata(component.metadata);
    const resourceDetails = {
      uri: getAiComponentMetadataString({
        metadata: component.metadata,
        field: 'uri',
      }),
      mimeType: getAiComponentMetadataString({
        metadata: component.metadata,
        field: 'mimeType',
      }),
      uriTemplates: metadata.uriTemplates ?? [],
      sampleContent: metadata.sampleContent ?? null,
    };

    return (
      <VStack gap={4} alignItems="stretch">
        <McpIdentityAccessDetails component={component} />
        <DrawerCollapsible title={t('resourceDetails')} defaultOpen>
          <MarkdownPreview source={formatJsonBlock(resourceDetails)} compact />
        </DrawerCollapsible>
      </VStack>
    );
  }

  return (
    <Box>
      <Text color="fg.muted" fontSize="sm">
        {t('unsupportedType')}
      </Text>
    </Box>
  );
}
